import type { Role } from "@/shared/schema/user.ts";
import type { BootstrapPayload } from "./bootContract.ts";

type RequiredAction = BootstrapPayload["required_action"];

const targets: Record<NonNullable<RequiredAction>, string> = {
  reset_password: "/force_reset_password",
  select_office: "/select/office",
  privacy_notice: "/privacy-notice-agreement",
};

// Bounce someone off /login and they can never log in.
const authFlowPages = [
  "/login",
  "/forgotpassword",
  "/resetpassword",
  "/unblock",
  "/auth",
];

// Only the controllers that skip ensure_office_and_agreed_to_privacy_notice, so
// narrower than authFlowPages: the filter runs on /login and /forgotpassword.
const requiredActionExempt = [
  "/login/otp",
  "/resetpassword",
  "/unblock",
  "/auth",
];

// Exact matches, not prefixes: the error pages beneath these stay reachable
// while signed in.
const signedInGoesHome = new Set([
  "/login",
  "/login/external",
  "/login/signed_out",
]);

// A login URL, but an admin-only page rather than a way in, so the
// signed-out exemption for /login must not keep anyone here.
const adminOnlyPage = "/login/as";

const noReturnTo = [
  "/resetpassword/",
  "/login",
  "/logout",
  "/session",
  "/agree_to_terms",
  "/select/office",
  "/api/v",
  "/pusher/auth",
  "/auth/token",
];

function matchesPage(pages: string[], pathname: string): boolean {
  return pages.some(
    (page) => pathname === page || pathname.startsWith(`${page}/`),
  );
}

function isAuthFlowPage(pathname: string): boolean {
  return matchesPage(authFlowPages, pathname);
}

// A gate firing here would bounce the user off the page that clears it.
function isRequiredActionPage(pathname: string): boolean {
  return matchesPage(Object.values(targets), pathname);
}

function skipsRequiredAction(pathname: string): boolean {
  return (
    matchesPage(requiredActionExempt, pathname) ||
    isRequiredActionPage(pathname)
  );
}

type BootLocation = Pick<Location, "pathname" | "search" | "hash">;

export function bootRedirectTarget(
  bootstrap: Pick<
    BootstrapPayload,
    | "logged_in"
    | "required_action"
    | "otp_pending"
    | "admin"
    | "impersonation_pending"
  >,
  location: BootLocation,
): string | null {
  // Rails and the router ignore a trailing slash; the matches below must too.
  const pathname = location.pathname.replace(/\/+$/, "") || "/";
  const { search, hash } = location;
  // The server keeps this in the session; a client redirect hands it over.
  const returnTo = encodeURIComponent(pathname + search + hash);

  // Mirrors require_admin_user, so the order of these branches matters.
  if (pathname === adminOnlyPage) {
    if (!bootstrap.logged_in) {
      return `/login?return_to=${returnTo}`;
    }

    if (bootstrap.required_action) {
      return targets[bootstrap.required_action];
    }

    if (bootstrap.otp_pending) {
      return `/login/otp?return_to=${returnTo}`;
    }

    return bootstrap.admin ? null : "/home";
  }

  if (!bootstrap.logged_in) {
    return isAuthFlowPage(pathname) ? null : `/login?return_to=${returnTo}`;
  }

  if (signedInGoesHome.has(pathname)) {
    return bootstrap.required_action
      ? targets[bootstrap.required_action]
      : "/home";
  }

  if (bootstrap.required_action && !skipsRequiredAction(pathname)) {
    return targets[bootstrap.required_action];
  }

  if (bootstrap.otp_pending && !isAuthFlowPage(pathname)) {
    return `/login/otp?return_to=${returnTo}`;
  }

  if (bootstrap.impersonation_pending && !isAuthFlowPage(pathname)) {
    return adminOnlyPage;
  }

  return null;
}

const RETURN_TO_KEY = "cvp-return-to";

// Targets that must not record where the visitor came from: the stash would be
// restored on the page these land on and send them straight back.
const endsElsewhere: ReadonlySet<string> = new Set([
  targets.reset_password,
  targets.select_office,
  "/home",
]);

// replace, like the server's 302: back must not land on a page that redirects
// here again. A parameter below rather than an import, because this file stays
// free of runtime imports and jsdom makes location.replace read-only.
function replaceLocation(path: string) {
  window.location.replace(path);
}

// Runs on every page. A no-op where the server gated before any HTML shipped,
// so in practice it redirects only on a shell.
export function enforceBootRedirect(
  bootstrap: BootstrapPayload,
  replace = replaceLocation,
): boolean {
  const target = bootRedirectTarget(bootstrap, window.location);

  if (!target) {
    return false;
  }

  // The fragment, which the server never receives, is why this is held here.
  if (!endsElsewhere.has(target)) {
    const { pathname, search, hash } = window.location;
    const here = pathname + search + hash;

    // The second gate of a chain - login, then OTP - sees only where the
    // server dropped the user, which is less than the first gate recorded.
    stashReturnTo(here);
  }

  replace(target);

  return true;
}

function stashReturnTo(value: string) {
  if (!isValidReturnTo(value) || storedReturnTo()) {
    return;
  }

  try {
    sessionStorage.setItem(RETURN_TO_KEY, value);
  } catch {
    // Without storage the user lands wherever the server sends them.
  }
}

// Rails keeps this in the session, which a shell cannot write to; restoreReturnTo
// spends it on the page the server sends the visitor to.
export function stashReturnToParam(search = window.location.search) {
  const returnTo = new URLSearchParams(search).get("return_to");

  if (returnTo) {
    stashReturnTo(returnTo);
  }
}

// Anyone can edit sessionStorage, so a stored destination is untrusted input.
// Stricter than the server, which has to allow /login/[a-z] for its own flows.
function isValidReturnTo(value: string): boolean {
  // A browser reads a backslash as a slash, so /\evil.example leaves the site,
  // and its URL parser strips tabs and newlines, so /\n/evil.example becomes
  // //evil.example.
  if (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    // eslint-disable-next-line no-control-regex -- rejecting them is the point
    /[\u{0}-\u{1F}\u{7F}]/u.test(value)
  ) {
    return false;
  }

  if (
    value === adminOnlyPage ||
    value.startsWith(`${adminOnlyPage}?`) ||
    value.startsWith(`${adminOnlyPage}#`)
  ) {
    return true;
  }

  return noReturnTo.every((rejected) => !value.includes(rejected));
}

function storedReturnTo(): string | null {
  let value: string | null;

  try {
    value = sessionStorage.getItem(RETURN_TO_KEY);
  } catch {
    return null;
  }

  return value && isValidReturnTo(value) ? value : null;
}

export function consumeReturnTo(): string | null {
  const value = storedReturnTo();

  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // Blocked storage, so there was nothing to read either.
  }

  return value;
}

export function restoreReturnTo(
  bootstrap: Pick<BootstrapPayload, "required_action">,
  replace = replaceLocation,
): boolean {
  const { pathname, search, hash } = window.location;

  // Before consuming: on the page that clears a gate, restoring would bounce
  // the user off it and straight back into the gate.
  if (
    bootstrap.required_action ||
    isAuthFlowPage(pathname) ||
    isRequiredActionPage(pathname)
  ) {
    return false;
  }

  const target = consumeReturnTo();

  if (!target || target === pathname + search + hash) {
    return false;
  }

  // A fragment-only navigation does not reload, so boot would render nothing.
  // The router reads the fragment when it is created, later in boot.
  const targetUrl = new URL(target, window.location.href);

  if (targetUrl.pathname + targetUrl.search === pathname + search) {
    window.history.replaceState(window.history.state, "", target);

    return false;
  }

  replace(target);

  return true;
}

// Must stay in sync with the backend's definition of an internal user.
const internalRoles: ReadonlySet<Role> = new Set([
  "limited_access",
  "consultant",
  "countrymanager",
  "referencemanager",
  "internationalmanager",
]);

export function internalUserRedirectTarget(
  bootstrap: Pick<BootstrapPayload, "current_user_roles">,
): string | null {
  const roles = bootstrap.current_user_roles;

  // No user to judge; bootRedirectTarget has already sent them to /login.
  if (!roles) {
    return null;
  }

  return roles.some((role) => internalRoles.has(role)) ? null : "/home";
}

export function enforceInternalUser(
  bootstrap: BootstrapPayload,
  replace = replaceLocation,
): boolean {
  const target = internalUserRedirectTarget(bootstrap);

  if (!target) {
    return false;
  }

  replace(target);

  return true;
}
